import { useEffect, useRef } from "react";
import styles from "./Headline.module.scss";
import { FadeInUpSmall } from "@/components/Animation";
import { customSanitize } from "@/utils/customSanitize";
import { Section } from "../Section/Section";

/**
 * Defines the possible HTML tags for the Headline component
 * @typedef {'h1-hero' | 'h1-pages' | 'h1-posts' | 'h2' | 'h3' | 'h2-small'} HeadlineTagType
 */
export type HeadlineTagType =
  | "h1-hero"
  | "h1-pages"
  | "h1-posts"
  | "h2"
  | "h3"
  | "h2-small";

/**
 * Props for the Headline component
 * @typedef {Object} HeadlineProps
 * @property {HeadlineTagType} headline_html_tag - The HTML tag to use for the headline
 * @property {string} headline - The headline text content (can include HTML)
 * @property {string} [extraClass] - Additional CSS classes to apply to the headline container
 * @property {string} [overline] - Optional text to display above the headline (can include HTML)
 * @property {string} [subline] - Optional text to display below the headline (can include HTML)
 * @property {boolean} [syncAnimation=false] - Whether to sync the animation with parent components
 * @property {string} [h1ClassName] - Additional CSS classes to apply to h1 elements only
 * @property {string} [strongColor] - Color to apply to <strong> elements within the headline
 * @property {boolean} [isQuote=false] - Whether the headline is a quote (applies specific styling)
 * @property {boolean} [wrapInSection=false] - Whether to wrap the headline in a Section component
 */
export type HeadlineProps = {
  headline_html_tag: HeadlineTagType;
  headline: string;
  /** extraClass Description about propName. */
  extraClass?: string;
  /** overline Description about propName. */
  overline?: string;
  subline?: string;
  syncAnimation?: boolean;
  h1ClassName?: string;
  strongColor?: string;
  isQuote?: boolean;
  wrapInSection?: boolean;
  disableAnimation?: boolean;
};

/**
 * Headline component for displaying titles with optional overline and subline text
 *
 * The component supports different HTML tags (h1, h2, h3) with variant styles,
 * animations, and additional customization options.
 *
 * @param {HeadlineProps} props - The component props
 * @returns {JSX.Element} The rendered Headline component
 *
 * @example
 * // Basic usage
 * <Headline headline_html_tag="h1-hero" headline="Main Title" />
 *
 * @example
 * // With overline and subline
 * <Headline
 *   headline_html_tag="h2"
 *   headline="Section Title"
 *   overline="CATEGORY"
 *   subline="Additional information about this section"
 * />
 *
 * @example
 * // With custom styling for strong elements
 * <Headline
 *   headline_html_tag="h1-pages"
 *   headline="Welcome to our <strong>Amazing</strong> Website"
 *   strongColor="#ff0000"
 * />
 */
export const Headline = ({
  headline_html_tag,
  headline,
  extraClass = "",
  overline,
  subline,
  syncAnimation = false,
  h1ClassName,
  strongColor,
  isQuote = false,
  wrapInSection = false,
  disableAnimation = false,
}: HeadlineProps) => {
  /**
   * Reference to the headline element for applying dynamic styles
   * @type {React.RefObject<HTMLHeadingElement>}
   */
  const titleRef = useRef<HTMLHeadingElement>(null);
  let headlineJSX = <></>;

  /**
   * Apply the strongColor to <strong> elements within the headline
   */
  useEffect(() => {
    if (titleRef.current && strongColor) {
      const pElement = titleRef.current.querySelector("strong");

      if (pElement) {
        // Adding styles directly to the <p> element
        pElement.style.color = strongColor;
      }
    }
  });

  {
    /* jscpd:ignore-start */
  }
  if (headline_html_tag.includes("h1") && headline) {
    headlineJSX = (
      <h1
        ref={titleRef}
        className={`${styles.title} ${headline_html_tag} ${h1ClassName || ""}`}
        dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
      ></h1>
    );
  } else if (headline_html_tag === "h2" && headline) {
    headlineJSX = (
      <h2
        ref={titleRef}
        className={`${styles.title} ${isQuote ? "quotes-banner-title" : ""}`}
        dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
      ></h2>
    );
  } else if (headline_html_tag === "h2-small" && headline) {
    headlineJSX = (
      <h2
        ref={titleRef}
        className={`h2-small ${styles.title} ${isQuote ? "quotes-banner-title" : ""}`}
        dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
      ></h2>
    );
  } else if (headline_html_tag === "h3" && headline) {
    headlineJSX = (
      <h3
        ref={titleRef}
        className={`${styles.title} ${isQuote ? "quotes-banner-title" : ""}`}
        dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
      ></h3>
    );
  }
  {
    /* jscpd:ignore-end */
  }

  const className = `${extraClass} ${subline ? `${headline_html_tag} ${styles["has-subline"]}` : ""} is-clipped`;

  const content = (
    <>
      {overline && (
        <span
          //needs both classes to work ( global and specific class)
          className={`overline overline-${headline_html_tag} ${isQuote ? "quotes-banner-subline" : ""}`}
          dangerouslySetInnerHTML={{ __html: customSanitize(overline) }}
        ></span>
      )}
      {headlineJSX}
      {subline && (
        <span
          //needs both classes to work ( global and specific class)
          className={`subline subline-${headline_html_tag} ${isQuote ? "quotes-banner-subline" : ""}`}
          dangerouslySetInnerHTML={{ __html: customSanitize(subline) }}
        ></span>
      )}
    </>
  );

  const inner = (
    <div className="is-clipped">
      {disableAnimation || syncAnimation ? (
        <div className={className}>{content}</div>
      ) : (
        <FadeInUpSmall className={className}>{content}</FadeInUpSmall>
      )}
    </div>
  );

  if (wrapInSection) {
    return (
      <Section tag="div" disableColumns className="py-0">
        {inner}
      </Section>
    );
  }

  return inner;
};
